/**
 * ModernServiceCard Component
 * Hexagonal service card with keyboard focus support
 */

import { motion } from 'framer-motion';
import clsx from 'clsx';
import {
  UtensilsCrossed, Utensils, Wine, Waves, Sparkles, Dumbbell, Compass, Car,
} from 'lucide-react';
import type { ServiceCardData } from '@/types';
import { useTVMotion } from '@/hooks/useTVMotion';

// Module-level icon map — created once, reused every render
const CARD_ICON_MAP: Record<string, React.ReactNode> = {
  card_room_service: <UtensilsCrossed className="w-5 h-5" />,
  card_restaurant:   <Utensils        className="w-5 h-5" />,
  card_bar:          <Wine            className="w-5 h-5" />,
  card_pool:         <Waves           className="w-5 h-5" />,
  card_spa:          <Sparkles        className="w-5 h-5" />,
  card_gym:          <Dumbbell        className="w-5 h-5" />,
  card_explore:      <Compass         className="w-5 h-5" />,
  card_transport:    <Car             className="w-5 h-5" />,
};

function getCardIcon(id: string) {
  return CARD_ICON_MAP[id] ?? <Sparkles className="w-5 h-5" />;
}

interface ModernServiceCardProps {
  data: ServiceCardData;
  isFocused: boolean;
  onClick?: () => void;
}

export function ModernServiceCard({
  data,
  isFocused,
  onClick
}: ModernServiceCardProps) {
  const tv = useTVMotion();
  return (
    <motion.div
      className={clsx(
        'service-card relative overflow-hidden rounded-2xl cursor-pointer h-full',
        'keyboard-focusable border border-white/[0.04]',
        { 'keyboard-focused': isFocused }
      )}
      whileHover={tv.hover({ scale: 1.02 })}
      animate={{
        scale: isFocused ? 1.04 : 1,
      }}
      transition={tv.entryTransition({
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1],
      })}
      onClick={onClick}
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${data.image})` }}
      />

      {/* Gradient Overlay — deeper, more cinematic */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10" />

      {/* Scan Line Effect — more subtle */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-zaxia-teal/20 to-transparent"
          animate={{
            y: isFocused ? ['-100%', '600%'] : '-100%',
          }}
          transition={{
            duration: 3,
            repeat: isFocused ? Infinity : 0,
            ease: 'linear',
          }}
        />
      </div>

      {/* Icon Badge */}
      <div className="service-card-icon absolute top-4 right-4 text-white/50">
        {getCardIcon(data.id)}
      </div>

      {/* Content */}
      <div className="service-card-content absolute bottom-0 left-0 right-0 p-5">
        <h3 className="font-display text-xl font-semibold text-white mb-1.5 tracking-tight">
          {data.title}
        </h3>
        <p className="text-sm text-white/60 leading-relaxed">
          {data.description}
        </p>
      </div>

      {/* Focus Glow — softer, more elegant */}
      {isFocused && (
        <motion.div
          className="absolute inset-0 pointer-events-none rounded-2xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          style={{
            boxShadow: 'inset 0 0 60px rgba(61, 145, 153, 0.25)',
          }}
        />
      )}
    </motion.div>
  );
}
